import React, { useState, useRef, useEffect } from 'react';
import { QASectionProps } from '../../types';
import { typography, layout, colors } from '../../styles/designTokens';
import QAItem from './QAItem';

const QASection: React.FC<QASectionProps> = ({ items, logoImage }) => {
  // State management - כל component מנהל את ה-state שלו
  // מתחיל ב-null כדי שאף שאלה לא תהיה פתוחה בהתחלה
  const [openQuestion, setOpenQuestion] = useState<number | null>(null);
  const [isTitleVisible, setIsTitleVisible] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const sectionRef = useRef<HTMLElement>(null);

  // IntersectionObserver for title animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsTitleVisible(true);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px',
      }
    );

    const currentRef = titleRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <section 
      ref={sectionRef}
      id="qa" 
      className="relative py-4 md:py-6 overflow-hidden" 
      style={{ 
        background: colors.accentSpecial.buttonGradientDiagonal, // גרדיאנט זהב-כתום כמו שהיה
        paddingTop: layout.sectionSpacing.mobile,
        paddingBottom: 'clamp(30px, 4vw, 60px)', // ✅ רווח מאוזן יותר: מובייל 30px → דסקטופ 60px - רקע כתום-זהב מתחת לשאלה האחרונה
        position: 'relative',
        overflowX: 'hidden', // מונע overflow אופקי
        width: '100%',
        maxWidth: '100%',
      }}
    >
      <div className="container mx-auto px-4 relative z-10" style={{ maxWidth: layout.contentMaxWidth }}>
        {/* Section Title - עם animation */}
        <h2 
          ref={titleRef}
          className="qa-title text-center font-black leading-tight"
          style={{ 
            fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
            fontFamily: typography.heading.fontFamily,
            letterSpacing: typography.heading.letterSpacing,
            color: '#000000',
            marginBottom: 'clamp(16px, 3vw, 32px)',
            opacity: isTitleVisible ? 1 : 0,
            transform: isTitleVisible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
            transition: 'opacity 0.8s cubic-bezier(0.34, 1.56, 0.64, 1), transform 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}
        >
          שאלות ותשובות
        </h2>

        {/* Q&A Items - עם scroll animations */}
        <div className="max-w-3xl mx-auto flex flex-col gap-4">
          {items.map((item, index) => (
            <QAItem
              key={index}
              item={item}
              index={index}
              isOpen={openQuestion === index}
              onToggle={() => setOpenQuestion(openQuestion === index ? null : index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default QASection;

